import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import AudioPlayer from './AudioPlayer';

const AudioClipDialog = ({ 
  isOpen, 
  onClose, 
  onClipSelected, 
  musicxmlId = null,
  allowedCategories = [], // Empty array means all categories allowed
  initialTab = 'library' // 'library', 'upload', or 'record'
}) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  
  const [activeTab, setActiveTab] = useState(initialTab); // 'record', 'upload', 'library'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Library tab state
  const [clips, setClips] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  
  // Upload tab state
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [newClipData, setNewClipData] = useState({
    name: '',
    description: '',
    category: '',
    tags: []
  });
  
  // Record tab state
  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioChunks, setAudioChunks] = useState([]);
  const [recordingTimer, setRecordingTimer] = useState(null);
  const [recordedAudioUrl, setRecordedAudioUrl] = useState(null);
  
  const fileInputRef = useRef(null);

  // Categories for dropdown
  const categories = [
    { value: 'technique', label: 'Vocal Technique' },
    { value: 'breathing', label: 'Breathing' },
    { value: 'pronunciation', label: 'Pronunciation' },
    { value: 'rhythm', label: 'Rhythm' },
    { value: 'pitch', label: 'Pitch' },
    { value: 'example', label: 'Example' },
    { value: 'exercise', label: 'Exercise' },
    { value: 'other', label: 'Other' }
  ];

  // Load clips when dialog opens
  useEffect(() => {
    if (isOpen && activeTab === 'library') {
      loadClips();
    }
  }, [isOpen, activeTab]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setActiveTab(initialTab);
      setError(null);
      setUploadFile(null);
      setUploadProgress(0);
      setNewClipData({ name: '', description: '', category: '', tags: [] });
      setSearchTerm('');
      setCategoryFilter('');
      // Reset recording state
      setIsRecording(false);
      setRecordedBlob(null);
      setRecordingDuration(0);
      setMediaRecorder(null);
      setAudioChunks([]);
      if (recordingTimer) {
        clearInterval(recordingTimer);
        setRecordingTimer(null);
      }
      if (recordedAudioUrl) {
        URL.revokeObjectURL(recordedAudioUrl);
        setRecordedAudioUrl(null);
      }
    }
  }, [isOpen, initialTab, recordingTimer, recordedAudioUrl]);

  const loadClips = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const params = new URLSearchParams({
        operation: 'getAudioClips',
        userId: user.id
      });
      
      if (musicxmlId) {
        params.append('musicxmlId', musicxmlId);
      }
      
      if (categoryFilter) {
        params.append('category', categoryFilter);
      }
      
      if (searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }
      
      const response = await fetch(`${API_BASE}/api/audio-clips?${params}`);
      
      if (response.ok) {
        const clipsData = await response.json();
        setClips(clipsData);
      } else {
        throw new Error('Failed to load audio clips');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/webm', 'audio/m4a'];
      if (!allowedTypes.includes(file.type)) {
        setError('Invalid file type. Please select an MP3, WAV, OGG, WebM, or M4A file.');
        return;
      }
      
      // Validate file size (50MB limit)
      if (file.size > 50 * 1024 * 1024) {
        setError('File too large. Please select a file smaller than 50MB.');
        return;
      }
      
      setUploadFile(file);
      setError(null);
      
      // Auto-populate name from filename if not set
      if (!newClipData.name) {
        const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
        setNewClipData(prev => ({ ...prev, name: nameWithoutExt }));
      }
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !newClipData.name.trim()) {
      setError('Please select a file and provide a name for the audio clip.');
      return;
    }
    
    setLoading(true);
    setError(null);
    setUploadProgress(0);
    
    try {
      // Step 1: Upload the file
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('userId', user.id);
      formData.append('userRole', user.role);

      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const uploadResponse = await fetch(`${API_BASE}/api/audio-clips?operation=uploadAudio`, {
        method: 'POST',
        body: formData
      });
      
      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json();
        throw new Error(errorData.error || 'Failed to upload file');
      }
      
      const uploadResult = await uploadResponse.json();
      setUploadProgress(50);
      
      // Step 2: Create the audio clip record
      const clipData = {
        name: newClipData.name.trim(),
        description: newClipData.description.trim() || null,
        fileUrl: uploadResult.fileUrl,
        fileName: uploadResult.fileName,
        fileSize: uploadResult.fileSize,
        mimeType: uploadResult.mimeType,
        category: newClipData.category || null,
        tags: newClipData.tags,
        musicxmlId: musicxmlId,
        creatorUserId: user.id
      };
      
      const createResponse = await fetch(`${API_BASE}/api/audio-clips?operation=createAudioClip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(clipData)
      });
      
      if (!createResponse.ok) {
        const errorData = await createResponse.json();
        throw new Error(errorData.error || 'Failed to create audio clip');
      }
      
      const createdClip = await createResponse.json();
      setUploadProgress(100);
      
      // Select the newly created clip
      if (onClipSelected) {
        onClipSelected(createdClip);
      }
      
      onClose();
      
    } catch (err) {
      setError(err.message);
      setUploadProgress(0);
    } finally {
      setLoading(false);
    }
  };

  const handleClipSelect = (clip) => {
    if (onClipSelected) {
      onClipSelected(clip);
    }
    onClose();
  };

  const handleTagAdd = (tag) => {
    if (tag.trim() && !newClipData.tags.includes(tag.trim())) {
      setNewClipData(prev => ({
        ...prev,
        tags: [...prev.tags, tag.trim()]
      }));
    }
  };

  const handleTagRemove = (tagToRemove) => {
    setNewClipData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } 
      });
      
      const recorder = new MediaRecorder(stream);
      const chunks = [];
      
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };
      
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        setRecordedBlob(blob);
        setAudioChunks([]);
        
        // Create URL for playback
        const url = URL.createObjectURL(blob);
        setRecordedAudioUrl(url);
        
        // Auto-populate name if not set
        if (!newClipData.name) {
          const timestamp = new Date().toLocaleString().replace(/[/,: ]/g, '-');
          setNewClipData(prev => ({ ...prev, name: `Recording-${timestamp}` }));
        }
        
        // Stop all tracks to release microphone
        stream.getTracks().forEach(track => track.stop());
      };
      
      recorder.start();
      setMediaRecorder(recorder);
      setAudioChunks(chunks);
      setIsRecording(true);
      setRecordingDuration(0);
      setError(null);
      
      // Start timer
      const timer = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
      setRecordingTimer(timer);
      
    } catch (err) {
      console.error('Error starting recording:', err);
      setError('Unable to access microphone. Please check your browser permissions and try again.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state === 'recording') {
      mediaRecorder.stop();
    }
    
    if (recordingTimer) {
      clearInterval(recordingTimer);
      setRecordingTimer(null);
    }
    
    setIsRecording(false);
  };

  const clearRecording = () => {
    if (recordedAudioUrl) {
      URL.revokeObjectURL(recordedAudioUrl);
      setRecordedAudioUrl(null);
    }
    setRecordedBlob(null);
    setRecordingDuration(0);
    setNewClipData(prev => ({ ...prev, name: '', description: '', category: '', tags: [] }));
  };

  const handleRecordedUpload = async () => {
    if (!recordedBlob || !newClipData.name.trim()) {
      setError('Please record audio and provide a name for the clip.');
      return;
    }
    
    setLoading(true);
    setError(null);
    setUploadProgress(0);
    
    try {
      // Step 1: Upload the recorded file
      const formData = new FormData();
      formData.append('file', recordedBlob, `${newClipData.name}.webm`);
      formData.append('userId', user.id);
      formData.append('userRole', user.role);

      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const uploadResponse = await fetch(`${API_BASE}/api/audio-clips?operation=uploadAudio`, {
        method: 'POST',
        body: formData
      });
      
      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json();
        throw new Error(errorData.error || 'Failed to upload recording');
      }
      
      const uploadResult = await uploadResponse.json();
      setUploadProgress(50);
      
      // Step 2: Create the audio clip record
      const clipData = {
        name: newClipData.name.trim(),
        description: newClipData.description.trim() || null,
        fileUrl: uploadResult.fileUrl,
        fileName: uploadResult.fileName,
        fileSize: uploadResult.fileSize,
        mimeType: uploadResult.mimeType,
        category: newClipData.category || null,
        tags: newClipData.tags,
        musicxmlId: musicxmlId,
        creatorUserId: user.id
      };
      
      const createResponse = await fetch(`${API_BASE}/api/audio-clips?operation=createAudioClip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(clipData)
      });
      
      if (!createResponse.ok) {
        const errorData = await createResponse.json();
        throw new Error(errorData.error || 'Failed to create audio clip');
      }
      
      const createdClip = await createResponse.json();
      setUploadProgress(100);
      
      // Select the newly created clip
      if (onClipSelected) {
        onClipSelected(createdClip);
      }
      
      onClose();
      
    } catch (err) {
      setError(err.message);
      setUploadProgress(0);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div 
        className="audio-clip-dialog"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#1f2937',
          borderRadius: '12px',
          width: '90%',
          maxWidth: '800px',
          maxHeight: '90vh',
          overflow: 'hidden',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header */}
        <div style={{
          background: '#111827',
          color: '#ffffff',
          padding: '16px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
          borderBottom: '1px solid #374151'
        }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
            🎵 Insert Audio Clip
          </h2>
          <button 
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#ffffff',
              fontSize: '24px',
              cursor: 'pointer',
              width: '32px',
              height: '32px',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>

        {/* Tab Navigation */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #374151',
          flexShrink: 0,
          background: '#1f2937'
        }}>
          {[
            { id: 'library', label: '🎵 Library', icon: '🎵' },
            { id: 'upload', label: '📁 Upload', icon: '📁' },
            { id: 'record', label: '🎤 Record', icon: '🎤' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                flex: 1,
                padding: '12px 16px',
                border: 'none',
                background: activeTab === tab.id ? '#374151' : 'transparent',
                borderBottom: activeTab === tab.id ? '2px solid #3b82f6' : '2px solid transparent',
                color: activeTab === tab.id ? '#3b82f6' : '#9ca3af',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s ease'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '24px', background: '#1f2937' }}>
          {error && (
            <div style={{
              background: '#7f1d1d',
              border: '1px solid #991b1b',
              color: '#fecaca',
              padding: '12px',
              borderRadius: '6px',
              marginBottom: '16px'
            }}>
              {error}
            </div>
          )}

          {/* Library Tab */}
          {activeTab === 'library' && (
            <div>
              {/* Search and Filters */}
              <div style={{
                display: 'flex',
                gap: '12px',
                marginBottom: '16px'
              }}>
                <input
                  type="text"
                  placeholder="Search clips..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    border: '1px solid #4b5563',
                    borderRadius: '6px',
                    fontSize: '14px',
                    background: '#374151',
                    color: '#f9fafb'
                  }}
                />
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #4b5563',
                    borderRadius: '6px',
                    fontSize: '14px',
                    minWidth: '120px',
                    background: '#374151',
                    color: '#f9fafb'
                  }}
                >
                  <option value="">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
                <button
                  onClick={loadClips}
                  disabled={loading}
                  style={{
                    padding: '8px 16px',
                    background: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: loading ? 'wait' : 'pointer'
                  }}
                >
                  {loading ? '⏳' : '🔍'}
                </button>
              </div>

              {/* Clips Grid */}
              {loading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
                  Loading clips...
                </div>
              ) : clips.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
                  No audio clips found. Try uploading or recording some!
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '4px' }}>
                  {clips.map(clip => (
                    <div
                      key={clip.id}
                      style={{
                        border: '1px solid #4b5563',
                        borderRadius: '4px',
                        padding: '4px 6px',
                        background: '#374151',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                      onClick={() => handleClipSelect(clip)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#3b82f6';
                        e.currentTarget.style.background = '#4b5563';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#4b5563';
                        e.currentTarget.style.background = '#374151';
                      }}
                    >
                      {/* Audio Player */}
                      <div style={{ flexShrink: 0 }}>
                        <AudioPlayer
                          clipName={clip.name}
                          fileUrl={clip.file_url}
                          compact={true}
                          showTitle={false}
                        />
                      </div>
                      
                      {/* Content */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '1px' }}>
                          <h4 style={{ margin: 0, fontSize: '12px', fontWeight: '600', color: '#f3f4f6', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '200px' }}>
                            {clip.name}
                          </h4>
                          {clip.category && (
                            <span style={{
                              padding: '1px 4px',
                              borderRadius: '8px',
                              fontSize: '8px',
                              background: '#4b5563',
                              color: '#d1d5db',
                              flexShrink: 0
                            }}>
                              {categories.find(cat => cat.value === clip.category)?.label || clip.category}
                            </span>
                          )}
                        </div>
                        {clip.description && (
                          <p style={{ margin: 0, color: '#9ca3af', fontSize: '9px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {clip.description}
                          </p>
                        )}
                      </div>
                      
                      {/* Select indicator */}
                      <div style={{ flexShrink: 0, fontSize: '10px', color: '#9ca3af' }}>
                        ➤
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Upload Tab */}
          {activeTab === 'upload' && (
            <div>
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#f3f4f6' }}>
                  Upload Audio File
                </h3>
                
                {/* File Selection */}
                <div style={{ marginBottom: '16px' }}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="audio/*"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      padding: '12px 24px',
                      background: uploadFile ? '#10b981' : '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    📁 {uploadFile ? 'Change File' : 'Select Audio File'}
                  </button>
                  {uploadFile && (
                    <div style={{ marginTop: '8px', fontSize: '14px', color: '#9ca3af' }}>
                      Selected: {uploadFile.name} ({(uploadFile.size / 1024 / 1024).toFixed(2)} MB)
                    </div>
                  )}
                </div>

                {/* Clip Details Form */}
                {uploadFile && (
                  <>
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px', color: '#d1d5db' }}>
                        Clip Name *
                      </label>
                      <input
                        type="text"
                        value={newClipData.name}
                        onChange={(e) => setNewClipData(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Enter a descriptive name for this clip"
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #4b5563',
                          borderRadius: '6px',
                          fontSize: '14px',
                          background: '#374151',
                          color: '#f9fafb'
                        }}
                      />
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>
                        Description
                      </label>
                      <textarea
                        value={newClipData.description}
                        onChange={(e) => setNewClipData(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Optional description of what this clip demonstrates"
                        rows={3}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px',
                          resize: 'vertical'
                        }}
                      />
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>
                        Category
                      </label>
                      <select
                        value={newClipData.category}
                        onChange={(e) => setNewClipData(prev => ({ ...prev, category: e.target.value }))}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                      >
                        <option value="">Select category...</option>
                        {categories.map(cat => (
                          <option key={cat.value} value={cat.value}>{cat.label}</option>
                        ))}
                      </select>
                    </div>

                    {/* Upload Progress */}
                    {uploadProgress > 0 && (
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{
                          width: '100%',
                          height: '8px',
                          background: '#e5e7eb',
                          borderRadius: '4px',
                          overflow: 'hidden'
                        }}>
                          <div
                            style={{
                              width: `${uploadProgress}%`,
                              height: '100%',
                              background: '#3b82f6',
                              transition: 'width 0.3s ease'
                            }}
                          />
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                          Uploading... {uploadProgress}%
                        </div>
                      </div>
                    )}

                    <button
                      onClick={handleUpload}
                      disabled={loading || !newClipData.name.trim()}
                      style={{
                        padding: '12px 24px',
                        background: loading || !newClipData.name.trim() ? '#9ca3af' : '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '14px',
                        cursor: loading || !newClipData.name.trim() ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {loading ? 'Uploading...' : 'Upload & Use Clip'}
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Record Tab */}
          {activeTab === 'record' && (
            <div>
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#f3f4f6' }}>
                  🎤 Record Audio Clip
                </h3>
                
                {/* Recording Controls */}
                <div style={{
                  textAlign: 'center',
                  padding: '32px',
                  background: '#374151',
                  borderRadius: '12px',
                  border: '1px solid #4b5563',
                  marginBottom: '24px'
                }}>
                  <div style={{
                    fontSize: '64px',
                    marginBottom: '16px',
                    opacity: isRecording ? '1' : '0.7'
                  }}>
                    🎤
                  </div>
                  
                  {/* Recording Status */}
                  <div style={{ marginBottom: '16px' }}>
                    {isRecording ? (
                      <div>
                        <div style={{
                          fontSize: '18px',
                          fontWeight: '600',
                          color: '#f87171',
                          marginBottom: '8px'
                        }}>
                          🔴 Recording...
                        </div>
                        <div style={{
                          fontSize: '24px',
                          fontFamily: 'monospace',
                          color: '#f3f4f6'
                        }}>
                          {formatDuration(recordingDuration)}
                        </div>
                      </div>
                    ) : recordedBlob ? (
                      <div>
                        <div style={{
                          fontSize: '18px',
                          fontWeight: '600',
                          color: '#34d399',
                          marginBottom: '8px'
                        }}>
                          ✅ Recording Complete
                        </div>
                        <div style={{
                          fontSize: '16px',
                          color: '#d1d5db'
                        }}>
                          Duration: {formatDuration(recordingDuration)}
                        </div>
                      </div>
                    ) : (
                      <div style={{
                        fontSize: '16px',
                        color: '#9ca3af'
                      }}>
                        Click the button below to start recording
                      </div>
                    )}
                  </div>
                  
                  {/* Control Buttons */}
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                    {!isRecording && !recordedBlob && (
                      <button
                        onClick={startRecording}
                        style={{
                          padding: '12px 24px',
                          background: '#dc2626',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '16px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        🎤 Start Recording
                      </button>
                    )}
                    
                    {isRecording && (
                      <button
                        onClick={stopRecording}
                        style={{
                          padding: '12px 24px',
                          background: '#6b7280',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '16px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        ⏹️ Stop Recording
                      </button>
                    )}
                    
                    {recordedBlob && (
                      <>
                        <button
                          onClick={clearRecording}
                          style={{
                            padding: '12px 24px',
                            background: '#6b7280',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '16px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}
                        >
                          🗑️ Clear
                        </button>
                      </>
                    )}
                  </div>
                </div>
                
                {/* Playback Preview */}
                {recordedAudioUrl && (
                  <div style={{ marginBottom: '24px' }}>
                    <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#f3f4f6' }}>
                      Preview Recording
                    </h4>
                    <audio 
                      controls 
                      src={recordedAudioUrl}
                      style={{
                        width: '100%',
                        height: '40px'
                      }}
                    />
                  </div>
                )}

                {/* Clip Details Form */}
                {recordedBlob && (
                  <>
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>
                        Clip Name *
                      </label>
                      <input
                        type="text"
                        value={newClipData.name}
                        onChange={(e) => setNewClipData(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Enter a descriptive name for this recording"
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                      />
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>
                        Description
                      </label>
                      <textarea
                        value={newClipData.description}
                        onChange={(e) => setNewClipData(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Optional description of what this recording demonstrates"
                        rows={3}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px',
                          resize: 'vertical'
                        }}
                      />
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>
                        Category
                      </label>
                      <select
                        value={newClipData.category}
                        onChange={(e) => setNewClipData(prev => ({ ...prev, category: e.target.value }))}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                      >
                        <option value="">Select category...</option>
                        {categories.map(cat => (
                          <option key={cat.value} value={cat.value}>{cat.label}</option>
                        ))}
                      </select>
                    </div>

                    {/* Upload Progress */}
                    {uploadProgress > 0 && (
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{
                          width: '100%',
                          height: '8px',
                          background: '#e5e7eb',
                          borderRadius: '4px',
                          overflow: 'hidden'
                        }}>
                          <div
                            style={{
                              width: `${uploadProgress}%`,
                              height: '100%',
                              background: '#3b82f6',
                              transition: 'width 0.3s ease'
                            }}
                          />
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                          Uploading... {uploadProgress}%
                        </div>
                      </div>
                    )}

                    <button
                      onClick={handleRecordedUpload}
                      disabled={loading || !newClipData.name.trim()}
                      style={{
                        padding: '12px 24px',
                        background: loading || !newClipData.name.trim() ? '#9ca3af' : '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '14px',
                        cursor: loading || !newClipData.name.trim() ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      {loading ? '⏳ Uploading...' : '💾 Save & Use Recording'}
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AudioClipDialog;